import { useState, useCallback, useRef } from 'react';
import { facebookApi, FacebookEmbeddedSignupConfig, FacebookEmbeddedSignupResult, FacebookPage } from '../services/facebookApi';

// Facebook SDK types are defined in @/types/window.d.ts

/**
 * State for the Facebook Messenger Embedded Signup flow
 */
interface EmbeddedSignupState {
  isLoading: boolean;
  isSDKLoaded: boolean;
  isSDKInitialized: boolean;
  error: string | null;
  result: FacebookEmbeddedSignupResult | null;
}

/**
 * Hook for Facebook Messenger Embedded Signup via Meta Platform
 *
 * Handles:
 * 1. Loading Facebook SDK dynamically
 * 2. Initializing SDK with config from backend
 * 3. Starting FB.login() with Messenger permissions
 * 4. Getting Facebook Pages linked to user account
 * 5. Completing signup flow with backend
 *
 * @example
 * ```tsx
 * const { startEmbeddedSignup, isLoading, error, result } = useFacebookEmbeddedSignup();
 *
 * if (result) {
 *   // Use result.accessToken, result.pageId
 * }
 * ```
 */
export function useFacebookEmbeddedSignup() {
  const [state, setState] = useState<EmbeddedSignupState>({
    isLoading: false,
    isSDKLoaded: false,
    isSDKInitialized: false,
    error: null,
    result: null,
  });

  const configRef = useRef<FacebookEmbeddedSignupConfig | null>(null);
  const pendingCodeRef = useRef<string | null>(null);
  const redirectUriRef = useRef<string | null>(null);

  // Store pages for selection flow
  const [availablePages, setAvailablePages] = useState<FacebookPage[]>([]);
  const [pageSelectionRequired, setPageSelectionRequired] = useState(false);

  /**
   * Load Facebook SDK script dynamically
   */
  const loadSDK = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.FB) {
        setState(prev => ({ ...prev, isSDKLoaded: true }));
        resolve();
        return;
      }

      // Check if script already exists
      if (document.getElementById('facebook-jssdk')) {
        // SDK script exists but FB not available yet - wait for it
        const checkFB = setInterval(() => {
          if (window.FB) {
            clearInterval(checkFB);
            setState(prev => ({ ...prev, isSDKLoaded: true }));
            resolve();
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkFB);
          reject(new Error('Facebook SDK load timeout'));
        }, 10000);
        return;
      }

      // Load SDK script
      window.fbAsyncInit = () => {
        setState(prev => ({ ...prev, isSDKLoaded: true }));
        resolve();
      };

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';

      script.onerror = () => {
        reject(new Error('Failed to load Facebook SDK'));
      };

      document.body.appendChild(script);
    });
  }, []);

  /**
   * Initialize Facebook SDK with config from backend
   */
  const initializeSDK = useCallback(async (): Promise<FacebookEmbeddedSignupConfig> => {
    if (!window.FB) {
      throw new Error('Facebook SDK not loaded');
    }

    // Get config from backend
    const config = await facebookApi.getEmbeddedSignupConfig();
    configRef.current = config;

    // Initialize FB SDK
    window.FB.init({
      appId: config.appId,
      cookie: true,
      xfbml: true,
      version: config.graphApiVersion,
    });

    setState(prev => ({ ...prev, isSDKInitialized: true }));
    console.log('[Facebook Embedded Signup] SDK initialized with config:', {
      appId: config.appId,
      version: config.graphApiVersion,
    });

    return config;
  }, []);

  /**
   * Complete the signup flow with selected Facebook Page
   */
  const completeSignup = useCallback(async (
    code: string,
    pageId: string
  ) => {
    try {
      console.log('[Facebook Embedded Signup] Completing signup with backend:', {
        pageId,
        redirectUri: redirectUriRef.current,
      });

      const result = await facebookApi.completeEmbeddedSignup({
        code,
        pageId,
        redirectUri: redirectUriRef.current || window.location.href.split('?')[0],
      });

      setState(prev => ({
        ...prev,
        isLoading: false,
        result,
        error: null,
      }));

      setPageSelectionRequired(false);
      setAvailablePages([]);

      console.log('[Facebook Embedded Signup] Signup completed successfully:', {
        webhookConfigured: result.webhookConfigured,
      });
    } catch (error: any) {
      console.error('[Facebook Embedded Signup] Failed to complete signup:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.message || error.message || 'Erro ao finalizar configuração',
      }));
    }
  }, []);

  /**
   * Handle Facebook Page selection (when multiple pages available)
   */
  const selectFacebookPage = useCallback(async (page: FacebookPage) => {
    if (!pendingCodeRef.current) {
      setState(prev => ({
        ...prev,
        error: 'Código de autorização expirado. Por favor, tente novamente.',
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));
    await completeSignup(pendingCodeRef.current, page.id);
  }, [completeSignup]);

  /**
   * Process FB.login response - handles async operations after receiving code
   * This is called separately from the FB.login callback to avoid async function type issues
   */
  const processLoginResponse = useCallback(async (code: string) => {
    try {
      // Store the redirect URI at the moment of login (before any navigation)
      // FB.login() uses the current page URL as redirect_uri when using response_type: 'code'
      const currentRedirectUri = window.location.href.split('?')[0];
      redirectUriRef.current = currentRedirectUri;

      // Get Facebook Pages linked to user
      console.log('[Facebook Embedded Signup] Fetching Facebook Pages...', { redirectUri: currentRedirectUri });
      const pages = await facebookApi.getFacebookPages(code);

      console.log('[Facebook Embedded Signup] Found pages:', pages.length);

      if (pages.length === 0) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Nenhuma página do Facebook encontrada. Certifique-se de ter uma Página do Facebook vinculada à sua conta.',
        }));
        return;
      }

      if (pages.length === 1) {
        // Single page - complete signup automatically
        await completeSignup(code, pages[0].id);
      } else {
        // Multiple pages - require selection
        setAvailablePages(pages);
        setPageSelectionRequired(true);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error: any) {
      console.error('[Facebook Embedded Signup] Failed to get pages:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.message || error.message || 'Erro ao buscar páginas do Facebook',
      }));
    }
  }, [completeSignup]);

  /**
   * Start the Embedded Signup flow
   */
  const startEmbeddedSignup = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null, result: null }));
    setPageSelectionRequired(false);
    setAvailablePages([]);

    try {
      // Step 1: Load SDK if needed
      if (!state.isSDKLoaded) {
        await loadSDK();
      }

      // Step 2: Initialize SDK with config from backend
      let config = configRef.current;
      if (!state.isSDKInitialized || !config) {
        config = await initializeSDK();
      }

      // Step 3: Start FB.login() with Messenger permissions
      // NOTE: FB.login callback MUST be a regular function, not async
      console.log('[Facebook Embedded Signup] Starting FB.login() with config_id:', config.configId);

      if (!window.FB) {
        throw new Error('Facebook SDK not available');
      }

      window.FB.login(
        (response: FBLoginResponse) => {
          console.log('[Facebook Embedded Signup] FB.login response:', {
            status: response.status,
            hasCode: !!response.authResponse?.code,
          });

          if (response.authResponse?.code) {
            const code = response.authResponse.code;
            pendingCodeRef.current = code;

            // Call async processing function separately
            // This avoids the "asyncfunction" type error from FB.login
            processLoginResponse(code);
          } else {
            // User cancelled or error
            setState(prev => ({
              ...prev,
              isLoading: false,
              error: response.status === 'not_authorized'
                ? 'Autorização negada pelo usuário'
                : 'Não foi possível obter autorização do Facebook',
            }));
          }
        },
        {
          config_id: config.configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            sessionInfoVersion: 2,
          },
        }
      );
    } catch (error: any) {
      console.error('[Facebook Embedded Signup] Error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Erro ao iniciar processo de login',
      }));
    }
  }, [state.isSDKLoaded, state.isSDKInitialized, loadSDK, initializeSDK, processLoginResponse]);

  /**
   * Reset state to initial values
   */
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      isSDKLoaded: state.isSDKLoaded, // Keep SDK loaded state
      isSDKInitialized: state.isSDKInitialized, // Keep SDK initialized state
      error: null,
      result: null,
    });
    pendingCodeRef.current = null;
    setAvailablePages([]);
    setPageSelectionRequired(false);
  }, [state.isSDKLoaded, state.isSDKInitialized]);

  return {
    // State
    state,
    isReady: !state.isLoading && !state.error,
    isLoading: state.isLoading,
    error: state.error,
    result: state.result,

    // Actions
    startEmbeddedSignup,
    reset,

    // SDK status
    isSDKLoaded: state.isSDKLoaded,
    isSDKInitialized: state.isSDKInitialized,

    // Page selection (for multiple pages)
    pageSelectionRequired,
    availablePages,
    selectFacebookPage,
  };
}
